"use client";

import {
  ArrowLeft,
  BarChart3,
  Brain,
  ChevronDown,
  Database,
  FolderKanban,
  HelpCircle,
  LogOut,
  Settings,
} from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { CacheManagementModal } from "@/components/aicheck/CacheManagementModal";
import { NotificationsBell } from "@/components/aicheck/NotificationsBell";
import { ProjectSearchBox } from "@/components/aicheck/ProjectSearchBox";
import UserIcon from "@/components/elements/shared/icons/UserIcon";
import { useAICheckLocalIdentity } from "@/hooks/useAICheckLocalIdentity";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { apiUrl } from "@/lib/aicheck/api-base";
import { authService } from "@/services/auth";
import { useAICheckStore } from "@/stores/aicheck";
import { useAuthStore } from "@/stores/auth";

export function Nav() {
  const pathname = usePathname();
  const router = useRouter();
  const xperisusUser = useAuthStore((s) => s.xperisusUser);
  const adminStatus = useIsAdmin();
  const { canViewKnowledge, reload: reloadIdentity } =
    useAICheckLocalIdentity();
  const storeProjectId = useAICheckStore((s) => s.projectId);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCacheManagement, setShowCacheManagement] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const prevPathnameRef = useRef<string>(pathname);

  useEffect(() => {
    if (!showUserMenu) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!userMenuRef.current?.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [showUserMenu]);

  // FIX 2: Re-fetch identity immediately after login redirect.
  // Nav is mounted at the root layout level; when the user is on /unauthenticated
  // the identity fetch returns anonymous (no session). After successful login the
  // router navigates away, but the [version] effect in useAICheckLocalIdentity
  // does not re-run automatically. Detecting the pathname transition and calling
  // reload() triggers a fresh identity fetch with the now-established session.
  useEffect(() => {
    const prev = prevPathnameRef.current;
    prevPathnameRef.current = pathname;
    if (prev === "/unauthenticated" && pathname !== "/unauthenticated") {
      reloadIdentity();
    }
  }, [pathname, reloadIdentity]);

  // FIX 1: Fetch the approval queue pending count once after roles are confirmed.
  // canViewKnowledge transitions false→true when the identity resolves (including
  // after the login-redirect reload above). 401/403 are silently ignored.
  useEffect(() => {
    if (!canViewKnowledge) return;
    let cancelled = false;
    void (async () => {
      try {
        const token = await authService.getIdToken();
        if (cancelled || !token) return;
        const res = await fetch(
          apiUrl("/api/aicheck/expert-knowledge/pending-count"),
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (cancelled || !res.ok) return;
        const data = (await res.json()) as { pending: number };
        if (!cancelled) setPendingCount(data.pending);
      } catch {
        // network or parse error — silently suppress, no badge shown
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canViewKnowledge]);

  // ログイン画面(/unauthenticated 配下)ではツールバーを出さない。
  // これらは認証ゲート外で、認証必須機能への導線を一切見せないため。
  if (pathname.startsWith("/unauthenticated")) {
    return null;
  }

  const handleSignOut = async () => {
    await authService.signOut();
    router.push("/unauthenticated");
  };

  const currentProjectId = Number(storeProjectId || 0) || null;

  return (
    <>
      {/* Sidebar */}
      <div className="w-16 bg-zinc-900 h-screen flex flex-col items-center py-6 gap-6 fixed left-0 top-0 z-50 text-zinc-400">
        <Link href="/" className="text-white font-bold text-xl mb-4">
          XP
        </Link>
      </div>

      {/* Header */}
      <header className="h-14 bg-zinc-900 border-b border-zinc-800 flex items-center justify-between px-6 pl-20 fixed top-0 w-full z-40">
        <div className="flex items-center gap-4">
          {/* ia-ui: ブランドと AI Check はトップページへの導線。結果表示中でも
              初期状態（検索・登録・案件一覧）に戻れるようフルリロードで遷移する */}
          <a
            href="/"
            className="text-white font-bold tracking-wider text-lg transition-opacity hover:opacity-80"
            title="トップページへ"
          >
            XPERISUS
          </a>
          <span className="text-zinc-600">|</span>
          <a
            href="/"
            className={`text-sm font-medium transition-colors ${
              pathname === "/" ? "text-white" : "text-zinc-400 hover:text-white"
            }`}
            title="トップページへ"
          >
            AI Check
          </a>
          <Link
            href="/projects"
            className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
              pathname.startsWith("/projects")
                ? "text-white"
                : "text-zinc-400 hover:text-white"
            }`}
          >
            <FolderKanban size={14} />
            案件
          </Link>
          <Link
            href="/guide"
            className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
              pathname.startsWith("/guide")
                ? "text-white"
                : "text-zinc-400 hover:text-white"
            }`}
            title="AI Check の使い方"
          >
            <HelpCircle size={14} />
            使い方
          </Link>
        </div>

        <div className="flex items-center gap-4">
          {/* Project search (itinerary id / tour code / tour name) */}
          <ProjectSearchBox initialValue={storeProjectId} />
          <NotificationsBell />

          {adminStatus === "admin" && (
            <>
              <div className="h-6 w-px bg-zinc-700" />

              {/* Config Link */}
              <Link
                href="/config"
                className={`flex items-center gap-1.5 text-sm transition-colors ${
                  pathname === "/config"
                    ? "text-white"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                <Settings size={14} />
                Config
              </Link>
            </>
          )}

          {canViewKnowledge && (
            <>
              <div className="h-6 w-px bg-zinc-700" />
              {pathname === "/expert-knowledge" && (
                <Link
                  href="/"
                  aria-label="AI Checkに戻る"
                  title="AI Checkに戻る"
                  className="inline-flex size-7 items-center justify-center rounded border border-zinc-700 text-zinc-400 transition-colors hover:border-zinc-600 hover:text-white"
                >
                  <ArrowLeft size={14} />
                </Link>
              )}
              <Link
                href="/expert-knowledge"
                className={`flex items-center gap-1.5 text-sm transition-colors ${
                  pathname === "/expert-knowledge"
                    ? "text-white"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                <Brain size={14} />
                Expert Knowledge
                {pendingCount != null && pendingCount > 0 && (
                  <span className="flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium leading-none text-white">
                    {pendingCount > 99 ? "99+" : pendingCount}
                  </span>
                )}
              </Link>
              <Link
                href="/usage"
                className={`flex items-center gap-1.5 text-sm transition-colors ${
                  pathname === "/usage"
                    ? "text-white"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                <BarChart3 size={14} />
                Usage
              </Link>
            </>
          )}

          <div className="h-6 w-px bg-zinc-700" />

          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => setShowUserMenu((open) => !open)}
              className="flex items-center gap-2 rounded-full border border-zinc-700 bg-zinc-800/80 px-2.5 py-1.5 text-sm text-zinc-300 transition-colors hover:border-zinc-600 hover:text-white"
            >
              <UserIcon src={xperisusUser?.icon_url} className="size-7" />
              <span className="max-w-40 truncate">
                {xperisusUser?.display_name ?? ""}
              </span>
              <ChevronDown size={14} className="text-zinc-500" />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 top-[calc(100%+0.5rem)] w-64 rounded-xl border border-zinc-200 bg-white p-2 shadow-xl">
                <div className="border-b border-zinc-100 px-3 py-2">
                  <div className="text-sm font-medium text-zinc-900">
                    {xperisusUser?.display_name ?? ""}
                  </div>
                  <div className="mt-0.5 text-xs text-zinc-500">
                    {currentProjectId
                      ? `Project ID: ${currentProjectId}`
                      : "No project selected"}
                  </div>
                </div>

                <div className="mt-1 space-y-1">
                  <button
                    type="button"
                    onClick={() => {
                      setShowUserMenu(false);
                      setShowCacheManagement(true);
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-zinc-700 transition-colors hover:bg-zinc-50 hover:text-zinc-900"
                  >
                    <Database size={15} />
                    Cache Management
                  </button>
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-zinc-700 transition-colors hover:bg-red-50 hover:text-red-700"
                  >
                    <LogOut size={15} />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <CacheManagementModal
        open={showCacheManagement}
        onClose={() => setShowCacheManagement(false)}
        currentProjectId={currentProjectId}
      />
    </>
  );
}
